import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Slider as AntdSlider, Tooltip } from 'antd';
import './slider.scss';
import TooltipContent from '@modules/insights/maturityAssessment/TooltipContent';

const TooltipDot = ({ title, data, placement = 'bottomLeft' }) => (
  <Tooltip
    title={<TooltipContent title={title} data={data} />}
    overlayClassName="maturity-assessment-tooltip-overlay"
    placement={placement}
    // getPopupContainer={() =>
    //   document.getElementsByClassName('MaturityAssessmentContainer')[0]
    // }
  >
    <span className="appkiticon icon-information-fill" />
  </Tooltip>
);

/**
 *
 * @param id {string} required
 * @param min {number} required
 * @param max {number} required
 * @param defaultValue {number}
 * @param healthyRange {array} e.g.[1.5, 2.5]
 * @param showHealthyRange {boolean}
 * @param onChange
 * @returns {JSX.Element}
 * @constructor
 */
export const Slider = ({
  id,
  min,
  max,
  defaultValue = 0,
  healthyRange,
  showHealthyRange = true,
  onChange,
  disabled,
  tooltipTitle,
  tooltipData,
}) => {
  const [value, setValue] = useState(defaultValue);

  const colorRail_active = '#0081E9';
  const colorRail_inactive = 'var(--assessmentSliderBgColor)';
  const colorTrack_active = '#E45C2B';
  // const colorTrack_overlap = '#003DAB';
  const colorTrack_overlap = '#0081E9';

  useLayoutEffect(() => {
    const container = document.getElementById(id);
    const rail = container.getElementsByClassName('ant-slider-rail')[0];
    const track = container.getElementsByClassName('ant-slider-track')[0];

    if (healthyRange && showHealthyRange) {
      const h0 = ((healthyRange[0] - min) / (max - min)) * 100; // healthy range low
      const h1 = ((healthyRange[1] - min) / (max - min)) * 100; // healthy range high
      rail.style.setProperty(
        'background',
        `linear-gradient(to right, ${colorRail_inactive} ${h0}%, ${colorRail_active} ${h0}% ${h1}%, ${colorRail_inactive} ${h1}%)`,
      );

      if (value > healthyRange[0] && value < healthyRange[1]) {
        const o = (1 - (value - healthyRange[0]) / (value - min)) * 100; // overlap point
        track.style.setProperty(
          'background',
          `linear-gradient(to right, ${colorTrack_active} ${o}%, ${colorTrack_overlap} ${o}%)`,
        );
      } else if (value >= healthyRange[1]) {
        const o0 = (1 - (value - healthyRange[0]) / (value - min)) * 100; // overlap point low
        const o1 = (1 - (value - healthyRange[1]) / (value - min)) * 100; // overlap point high
        track.style.setProperty(
          'background',
          `linear-gradient(to right, ${colorTrack_active} ${o0}%, ${colorTrack_overlap} ${o0}% ${o1}%, ${colorTrack_active} ${o1}%)`,
        );
      } else {
        track.style.setProperty(
          'background',
          `linear-gradient(to right, ${colorTrack_active}, ${colorTrack_active})`,
        ); // 同样的颜色避免不用linear-gradient的时候有white flash
      }
    } else {
      rail.style.setProperty(
        'background',
        `linear-gradient(to right, ${colorRail_inactive}, ${colorRail_inactive} )`,
      );
      track.style.setProperty(
        'background',
        `linear-gradient(to right, ${colorTrack_active}, ${colorTrack_active})`,
      ); // 同样的颜色避免不用linear-gradient的时候有white flash
    }
  }, [id, min, max, healthyRange, showHealthyRange, value]);

  const marks = useMemo(() => {
    const tooltip = <TooltipDot title={tooltipTitle} data={tooltipData} />;
    const markObject = {};
    for (let i = min; i <= max; i++) {
      markObject[i] = tooltip;
    }
    return markObject;
  }, [tooltipTitle, tooltipData]);

  return (
    <div id={id} className="Slider">
      <AntdSlider
        dots
        min={min}
        max={max}
        defaultValue={defaultValue}
        marks={marks}
        tooltipVisible={false}
        onChange={(sliderValue) => {
          setValue(sliderValue);
          typeof onChange === 'function' && onChange(sliderValue, id);
        }}
        disabled={disabled}
      />
    </div>
  );
};
